import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, RevealOnView } from '@/components/ui';
import { BUTTON_SLIDE, CONTENT_SLIDE, HEADER_FADE, useInView } from '@/utils/animation';

const ROW_KEYS = [
  'mission',
  'history',
  'vision',
  'partnership',
  'recognition',
  'our-group',
] as const;

export function StorySection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLElement>(null);
  const inView = useInView(sectionRef, { threshold: 0 });
  const [mountReady, setMountReady] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setMountReady(true), 100);
    return () => clearTimeout(timer);
  }, []);
  const visible = inView && mountReady;

  return (
    <section
      ref={sectionRef}
      aria-labelledby="story-heading"
      className="bg-clearlab-oat px-6 py-30 md:px-14 md:py-30 lg:px-20 lg:py-50 xl:px-30 xl:py-50"
    >
      <div className="mx-auto flex w-full max-w-content-865 flex-col gap-20 md:flex-row md:items-start md:justify-between md:gap-10 lg:mx-0 lg:max-w-none xl:mx-auto xl:max-w-content-865">
        <h2
          id="story-heading"
          style={{
            opacity: visible ? 1 : 0,
            transition: `opacity ${HEADER_FADE.durationMs}ms ${HEADER_FADE.ease}`,
          }}
          className="shrink-0 text-heading-24 font-semibold tracking-tight text-clearlab-steel/40 md:w-60 xl:ml-[62px]"
        >
          {t('home.story.heading')}
        </h2>

        <RevealOnView
          {...CONTENT_SLIDE}
          inView={visible}
          delayMs={Math.round(HEADER_FADE.durationMs * 0.25)}
          className="flex flex-1 flex-col gap-20 md:max-w-[600px] xl:max-w-[520px]"
        >
          {ROW_KEYS.map((rowKey, idx) => {
            const body = t(`home.story.rows.${rowKey}.body`, { returnObjects: true }) as string[];
            const table = t(`home.story.rows.${rowKey}.table`, {
              returnObjects: true,
              defaultValue: undefined,
            }) as
              | {
                  columns: { key: string; label: string }[];
                  rows: Record<string, string | string[]>[];
                }
              | undefined;
            return (
              <article key={rowKey} className="flex flex-col gap-6 text-clearlab-midnight">
                <h3 className="w-60 text-label-16 font-bold tracking-tight">
                  {t(`home.story.rows.${rowKey}.title`)}
                </h3>
                <div className="flex flex-col gap-6 text-label-16">
                  {body.map((paragraph, i) => (
                    <p key={i} className="leading-[44px]">
                      {paragraph}
                    </p>
                  ))}

                  {table?.columns?.length ? (
                    <div className="mt-6 w-full overflow-x-auto [container-type:inline-size]">
                      <table className="w-full border-separate border-spacing-0 text-[16px] font-normal leading-[20px] text-clearlab-midnight">
                        <thead>
                          <tr>
                            {table.columns.map((col, index) => {
                              const shrinkClass =
                                col.key === 'location'
                                  ? 'w-px whitespace-nowrap'
                                  : col.key === 'role'
                                    ? 'w-px'
                                    : '';
                              return (
                                <th
                                  key={col.key}
                                  className={`border-b border-clearlab-divider-darker pb-2 text-left text-[12px] font-semibold text-clearlab-steel ${shrinkClass} ${index > 0 ? 'pl-[clamp(40px,calc(6cqw_+_16px),56px)]' : ''}`}
                                >
                                  {col.label}
                                </th>
                              );
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {table.rows.map((row, i) => (
                            <tr key={i}>
                              {table.columns.map((col, index) => {
                                const value = row[col.key];
                                const shrinkClass =
                                  col.key === 'location'
                                    ? 'w-px whitespace-nowrap'
                                    : col.key === 'role'
                                      ? 'w-px'
                                      : '';
                                return (
                                  <td
                                    key={col.key}
                                    className={`align-top ${i === 0 ? 'pb-4 pt-6' : 'py-4'} ${shrinkClass} ${index > 0 ? 'pl-[clamp(40px,calc(6cqw_+_16px),56px)]' : ''}`}
                                  >
                                    {Array.isArray(value)
                                      ? value.map((part, partIdx) => (
                                          <span key={partIdx}>
                                            {part}
                                            {partIdx < value.length - 1 && <br />}
                                          </span>
                                        ))
                                      : value}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                </div>

                {idx < ROW_KEYS.length - 1 && (
                  <hr className="mt-2 w-6 border-0 border-t border-clearlab-divider-darker/60 md:hidden" />
                )}
              </article>
            );
          })}
          <RevealOnView {...BUTTON_SLIDE}>
            <Button to="/contact" variant="outlined" size="large" tone="blue" iconRight>
              {t('home.story.cta')}
            </Button>
          </RevealOnView>
        </RevealOnView>
      </div>
    </section>
  );
}
